import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { Home, Calculator, Search, FileText, HelpCircle } from 'lucide-react';
import SEOHead from './SEOHead';

// 404 content in all 8 languages
const NOT_FOUND_CONTENT = {
  it: {
    title: 'Pagina Non Trovata',
    heading: 'Oops! 404',
    message: 'La pagina che stai cercando non esiste o è stata spostata.',
    suggestion: 'Prova uno di questi strumenti:',
    homeLink: 'Torna alla Home',
    tools: [
      { name: 'Calcola Codice Fiscale', path: '/', icon: Calculator },
      { name: 'Verifica CF', path: '/verifica', icon: Search },
      { name: 'Guida Completa', path: '/guida-codice-fiscale', icon: FileText },
      { name: 'FAQ', path: '/faq', icon: HelpCircle }
    ]
  },
  en: {
    title: 'Page Not Found',
    heading: 'Oops! 404',
    message: 'The page you are looking for does not exist or has been moved.',
    suggestion: 'Try one of these tools:',
    homeLink: 'Back to Home',
    tools: [
      { name: 'Calculate Tax Code', path: '/en', icon: Calculator },
      { name: 'Verify Tax Code', path: '/en/verify', icon: Search },
      { name: 'Complete Guide', path: '/en/tax-code-guide', icon: FileText },
      { name: 'FAQ', path: '/en/faq', icon: HelpCircle }
    ]
  },
  de: {
    title: 'Seite Nicht Gefunden',
    heading: 'Oops! 404',
    message: 'Die Seite, die Sie suchen, existiert nicht oder wurde verschoben.',
    suggestion: 'Probieren Sie eines dieser Tools:',
    homeLink: 'Zurück zur Startseite',
    tools: [
      { name: 'Steuernummer Berechnen', path: '/de', icon: Calculator },
      { name: 'Steuernummer Prüfen', path: '/de/verifizieren', icon: Search },
      { name: 'Vollständige Anleitung', path: '/de/steuernummer-anleitung', icon: FileText },
      { name: 'FAQ', path: '/de/faq', icon: HelpCircle }
    ]
  },
  fr: {
    title: 'Page Non Trouvée',
    heading: 'Oops! 404',
    message: 'La page que vous recherchez n\'existe pas ou a été déplacée.',
    suggestion: 'Essayez l\'un de ces outils:',
    homeLink: 'Retour à l\'Accueil',
    tools: [
      { name: 'Calculer Code Fiscal', path: '/fr', icon: Calculator },
      { name: 'Vérifier Code Fiscal', path: '/fr/verifier', icon: Search },
      { name: 'Guide Complet', path: '/fr/guide-code-fiscal', icon: FileText },
      { name: 'FAQ', path: '/fr/faq', icon: HelpCircle }
    ]
  },
  es: {
    title: 'Página No Encontrada',
    heading: 'Oops! 404',
    message: 'La página que buscas no existe o ha sido movida.',
    suggestion: 'Prueba una de estas herramientas:',
    homeLink: 'Volver al Inicio',
    tools: [
      { name: 'Calcular Código Fiscal', path: '/es', icon: Calculator },
      { name: 'Verificar Código Fiscal', path: '/es/verificar', icon: Search },
      { name: 'Guía Completa', path: '/es/guia-codigo-fiscal', icon: FileText },
      { name: 'FAQ', path: '/es/faq', icon: HelpCircle }
    ]
  },
  pt: {
    title: 'Página Não Encontrada',
    heading: 'Oops! 404',
    message: 'A página que você procura não existe ou foi movida.',
    suggestion: 'Experimente uma destas ferramentas:',
    homeLink: 'Voltar ao Início',
    tools: [
      { name: 'Calcular Código Fiscal', path: '/pt', icon: Calculator },
      { name: 'Verificar Código Fiscal', path: '/pt/verificar', icon: Search },
      { name: 'Guia Completo', path: '/pt/guia-codigo-fiscal', icon: FileText },
      { name: 'FAQ', path: '/pt/faq', icon: HelpCircle }
    ]
  },
  ro: {
    title: 'Pagină Negăsită',
    heading: 'Oops! 404',
    message: 'Pagina pe care o căutați nu există sau a fost mutată.',
    suggestion: 'Încercați unul dintre aceste instrumente:',
    homeLink: 'Înapoi la Pagina Principală',
    tools: [
      { name: 'Calculează Codul Fiscal', path: '/ro', icon: Calculator },
      { name: 'Verifică Codul Fiscal', path: '/ro/verifica', icon: Search },
      { name: 'Ghid Complet', path: '/ro/ghid-cod-fiscal', icon: FileText },
      { name: 'FAQ', path: '/ro/faq', icon: HelpCircle }
    ]
  },
  pl: {
    title: 'Strona Nie Znaleziona',
    heading: 'Oops! 404',
    message: 'Strona, której szukasz, nie istnieje lub została przeniesiona.',
    suggestion: 'Wypróbuj jedno z tych narzędzi:',
    homeLink: 'Powrót do Strony Głównej',
    tools: [
      { name: 'Oblicz Kod Podatkowy', path: '/pl', icon: Calculator },
      { name: 'Zweryfikuj Kod Podatkowy', path: '/pl/zweryfikuj', icon: Search },
      { name: 'Pełny Przewodnik', path: '/pl/przewodnik-kod-podatkowy', icon: FileText },
      { name: 'FAQ', path: '/pl/faq', icon: HelpCircle }
    ]
  }
};

const NotFoundPage = () => {
  const { locale } = useI18n();
  const content = NOT_FOUND_CONTENT[locale] || NOT_FOUND_CONTENT.en;

  return (
    <>
      <SEOHead
        routeKey="home"
        customTitle={`404 - ${content.title} | CodiceFiscale.expert`}
        customDescription={content.message}
      />

      <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
        <div className="text-center max-w-2xl mx-auto">
          {/* 404 Heading */}
          <div className="mb-8">
            <h1 className="text-8xl md:text-9xl font-bold text-emerald-600 opacity-20">
              404
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 -mt-8">
              {content.heading}
            </h2>
          </div>

          {/* Message */}
          <p className="text-lg text-gray-600 mb-8">
            {content.message}
          </p>

          {/* Suggestion */}
          <p className="text-sm text-gray-500 mb-6">
            {content.suggestion}
          </p>

          {/* Tool Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {content.tools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={index}
                  to={tool.path}
                  className="flex flex-col items-center p-4 bg-gray-50 hover:bg-emerald-50 rounded-lg transition-colors group"
                >
                  <Icon className="h-8 w-8 text-gray-400 group-hover:text-emerald-600 mb-2 transition-colors" />
                  <span className="text-sm text-gray-700 group-hover:text-emerald-700 text-center transition-colors">
                    {tool.name}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Home Button */}
          <Link
            to={locale === 'it' ? '/' : `/${locale}`}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <Home className="h-5 w-5" />
            {content.homeLink}
          </Link>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage;
